/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react-refresh/only-export-components */
import { systemRoles } from "@/atoms";
import { AuthApi } from "@/axios.config.ts";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import Logo from "@/components/common/Logo";
import { UserRole } from "@/types";
import { IUser, Role } from "@/types/user.type";
import { getUseRoute } from "@/utils/userroutes/loginroutes2";
import { notifications } from "@mantine/notifications";
import Cookies from "js-cookie";
import React, { createContext, useContext, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";

interface IAuthContext {
  user: IUser | null;
  initialLoading: boolean;
  logout: () => Promise<void>;
  getRoles: () => Promise<void>;
  loading: boolean;
  profile: {
    user: IUser;
    employee: any;
    roles: Role[];
  } | null;
  isAdmin: boolean;
}

const AuthContext = createContext<IAuthContext | undefined>(undefined);

export const nonProtectedRoutes = [
  "/HomePage",
  "/verify-certificate",
  "/auth/login",
  "/auth/forgot-password",
  "/auth/verify-email",
  "/auth/reset-password",
  "/auth/forgot-password-bylicense",
  "/auth/accept-invitation",
  "/create-account/CreateAccount",
  "/certificate-details/ViewCertificateDetails",
  "/auth/reset-password",
];

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<IUser | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [roles, setRoles] = useRecoilState(systemRoles);
  const [isAdmin, setIsAdmin] = useState(false);
  const token = Cookies.get("token");
  const isWhiteListed = nonProtectedRoutes.some((route) =>
    location.pathname.startsWith(route)
  );

  const getProfile = async () => {
    setLoading(true);
    try {
      const res = await AuthApi.get("/auth/profile");

      setUser(res.data.data.user);
      setProfile(res.data.data);
      setIsAdmin(
        res.data.data.roles.some(
          (role: Role) => role.roleName === UserRole.ADMIN
        )
      );
    } catch (error) {
      console.log(error);
      setInitialLoading(false);
    }
    setLoading(false);
  };

  const getRoles = async () => {
    try {
      const res = await AuthApi.get("/roles/all");
      setRoles(res.data.data as Role[]);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (!roles || !roles.length) getRoles();
    if (token) getProfile();
  }, []);

  useEffect(() => {
    if (token && roles.length && profile && user) setInitialLoading(false);
    else if (!token) setInitialLoading(false);
  }, [roles, user, profile]);

  const logout = async () => {
    setLoading(true);
    setUser(null);
    Cookies.remove("token");
    setLoading(false);
    notifications.show({
      title: "Success",
      message: "Logged out successfully",
      color: "green",
    });
    navigate("/auth/login");
    window.location.reload();
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-screen ">
        <div className="animate-pulse flex flex-col  items-center">
          <Logo />
          <div className="flex items-center mt-4 gap-4">
            <LoadingSpinner />
            <h1 className="opacity-60"> Loading...</h1>
          </div>
        </div>
      </div>
    );
  }
  if (isWhiteListed && user && token) {
    const nextUrl = getUseRoute(profile.roles[0].roleName);
    return <Navigate to={nextUrl} />;
  }

  if (!user && !initialLoading && !isWhiteListed) {
    return <Navigate to="/auth/login" />;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        getRoles,
        initialLoading,
        logout,
        loading,
        profile,
        isAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export default function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
